import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { StatusBadge, Spinner } from "../../components/Brand";
import { currency, fmtDate } from "../../lib/status";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

function Modal({ open, onClose, title, children, testid }) {
    if (!open) return null;
    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
            data-testid={testid}
        >
            <div className="bg-[#180A21] border border-[#2B1438] w-full max-w-md max-h-[90vh] overflow-y-auto">
                <div className="px-6 py-4 border-b border-[#2B1438] flex items-center justify-between">
                    <div className="font-serif text-lg text-white">{title}</div>
                    <button
                        onClick={onClose}
                        className="text-white/60 hover:text-white"
                        data-testid={`${testid}-close`}
                    >
                        ✕
                    </button>
                </div>
                <div className="p-6">{children}</div>
            </div>
        </div>
    );
}

const inputCls =
    "w-full px-3 py-2 bg-[#0F0614] border border-[#2B1438] text-white text-sm focus:border-[#D4AF37] focus:outline-none";

export default function AdminBookings() {
    const [bookings, setBookings] = useState(null);
    const [techs, setTechs] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [services, setServices] = useState([]);
    const [statusFilter, setStatusFilter] = useState("");
    const [assignFor, setAssignFor] = useState(null);
    const [assignTo, setAssignTo] = useState("");

    // Reschedule
    const [rescheduleFor, setRescheduleFor] = useState(null);
    const [newAt, setNewAt] = useState("");
    const [rescheduleReason, setRescheduleReason] = useState("");

    // Manual booking
    const [manualOpen, setManualOpen] = useState(false);
    const [manual, setManual] = useState({
        customer_id: "",
        service_id: "",
        scheduled_at: "",
        technician_id: "",
        payment_method: "cash",
        line1: "",
        city: "",
        country: "AE",
        notes: "",
    });

    const load = async () => {
        const params = {};
        if (statusFilter) params.status = statusFilter;
        const { data } = await api.get("/bookings", { params });
        setBookings(data);
    };

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const params = {};
                if (statusFilter) params.status = statusFilter;
                const [b, t, c, s] = await Promise.all([
                    api.get("/bookings", { params }),
                    api.get("/technicians"),
                    api.get("/customers").catch(() => ({ data: [] })),
                    api.get("/services").catch(() => ({ data: [] })),
                ]);
                if (cancelled) return;
                setBookings(b.data);
                setTechs(t.data);
                setCustomers(c.data);
                setServices(s.data);
            } catch (e) {
                if (!cancelled) toast.error(formatApiError(e.response?.data?.detail));
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [statusFilter]);

    if (!bookings) return <Spinner />;

    const doAssign = async () => {
        if (!assignTo) return;
        try {
            await api.post(`/bookings/${assignFor}/assign`, { technician_id: assignTo });
            toast.success("Assigned");
            setAssignFor(null);
            setAssignTo("");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const doReschedule = async () => {
        if (!newAt) return;
        try {
            await api.post(`/bookings/${rescheduleFor.id}/reschedule`, {
                scheduled_at: new Date(newAt).toISOString(),
                reason: rescheduleReason || null,
            });
            toast.success("Rescheduled");
            setRescheduleFor(null);
            setNewAt("");
            setRescheduleReason("");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const submitManual = async () => {
        if (!manual.customer_id || !manual.service_id || !manual.scheduled_at) {
            toast.error("Customer, service and date are required");
            return;
        }
        try {
            const payload = {
                customer_id: manual.customer_id,
                service_id: manual.service_id,
                scheduled_at: new Date(manual.scheduled_at).toISOString(),
                payment_method: manual.payment_method,
                notes: manual.notes || null,
                technician_id: manual.technician_id || null,
                address: {
                    label: "Service",
                    line1: manual.line1 || "—",
                    city: manual.city || "—",
                    country: manual.country || "AE",
                },
            };
            const { data } = await api.post("/bookings/manual", payload);
            toast.success(`Booking ${data.booking_number} created`);
            setManualOpen(false);
            setManual({
                customer_id: "",
                service_id: "",
                scheduled_at: "",
                technician_id: "",
                payment_method: "cash",
                line1: "",
                city: "",
                country: "AE",
                notes: "",
            });
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const downloadCsv = async (path, filename) => {
        try {
            const res = await api.get(path, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="lx-label text-[#D4AF37] mb-2">Operations</div>
                    <h1 className="font-serif text-4xl text-white">Bookings</h1>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <button
                        data-testid="adm-csv-bookings"
                        onClick={() => downloadCsv("/reports/bookings.csv", "bookings.csv")}
                        className="px-3 py-2.5 border border-[#2B1438] text-white/70 text-xs hover:text-white"
                    >
                        ⬇ Bookings CSV
                    </button>
                    <button
                        data-testid="adm-csv-payouts"
                        onClick={() => downloadCsv("/reports/payouts.csv", "payouts.csv")}
                        className="px-3 py-2.5 border border-[#2B1438] text-white/70 text-xs hover:text-white"
                    >
                        ⬇ Payouts CSV
                    </button>
                    <button
                        data-testid="adm-csv-kyc"
                        onClick={() => downloadCsv("/reports/kyc.csv", "kyc.csv")}
                        className="px-3 py-2.5 border border-[#2B1438] text-white/70 text-xs hover:text-white"
                    >
                        ⬇ KYC CSV
                    </button>
                    <button
                        data-testid="adm-create-manual-btn"
                        onClick={() => setManualOpen(true)}
                        className="lx-btn-primary px-4 py-2.5 text-xs"
                    >
                        + Manual booking
                    </button>
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        data-testid="adm-bookings-status-filter"
                        className="bg-[#1C0E24] border border-[#2B1438] text-white px-3 py-2.5 text-sm"
                    >
                        <option value="">All statuses</option>
                        {[
                            "confirmed",
                            "technician_assigned",
                            "technician_on_the_way",
                            "job_started",
                            "job_completed",
                            "payment_completed",
                            "cancelled",
                        ].map((s) => (
                            <option key={s} value={s}>
                                {s}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="lx-card-dark overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left text-white/60 border-b border-[#2B1438]">
                            <th className="p-4 lx-label">Booking</th>
                            <th className="p-4 lx-label">Customer</th>
                            <th className="p-4 lx-label">Service</th>
                            <th className="p-4 lx-label">Technician</th>
                            <th className="p-4 lx-label">Status</th>
                            <th className="p-4 lx-label text-right">Amount</th>
                            <th className="p-4" />
                        </tr>
                    </thead>
                    <tbody>
                        {bookings.map((b) => (
                            <tr
                                key={b.id}
                                data-testid={`adm-booking-row-${b.booking_number}`}
                                className="border-b border-[#2B1438] hover:bg-white/5"
                            >
                                <td className="p-4">
                                    <div className="text-white">{b.booking_number}</div>
                                    <div className="text-xs text-white/60">
                                        {fmtDate(b.scheduled_at)}
                                    </div>
                                </td>
                                <td className="p-4 text-white/80">{b.customer?.name}</td>
                                <td className="p-4 text-white/80">{b.service?.name}</td>
                                <td className="p-4 text-white/80">
                                    {b.technician?.name || (
                                        <span className="text-[#FF9500]">Unassigned</span>
                                    )}
                                </td>
                                <td className="p-4">
                                    <StatusBadge status={b.status} />
                                </td>
                                <td className="p-4 text-right text-white font-serif">
                                    {currency(b.total_amount, b.currency)}
                                </td>
                                <td className="p-4 text-right space-x-3">
                                    <Link
                                        to={`/customer/bookings/${b.id}`}
                                        data-testid={`adm-details-${b.booking_number}`}
                                        className="text-xs text-white/70 hover:text-[#D4AF37] hover:underline"
                                    >
                                        Details
                                    </Link>
                                    <button
                                        onClick={() => setAssignFor(b.id)}
                                        data-testid={`adm-assign-${b.booking_number}`}
                                        className="text-xs text-[#D4AF37] hover:underline"
                                    >
                                        Assign
                                    </button>
                                    <button
                                        onClick={() => {
                                            setRescheduleFor(b);
                                            try {
                                                setNewAt(
                                                    new Date(b.scheduled_at)
                                                        .toISOString()
                                                        .slice(0, 16),
                                                );
                                            } catch (e) {
                                                setNewAt("");
                                            }
                                        }}
                                        data-testid={`adm-reschedule-${b.booking_number}`}
                                        className="text-xs text-[#D4AF37] hover:underline"
                                    >
                                        Reschedule
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {bookings.length === 0 && (
                    <div className="p-12 text-center text-white/60">No bookings.</div>
                )}
            </div>

            {/* Assign modal */}
            <Modal
                open={!!assignFor}
                onClose={() => setAssignFor(null)}
                title="Assign technician"
                testid="adm-assign-modal"
            >
                <select
                    value={assignTo}
                    onChange={(e) => setAssignTo(e.target.value)}
                    data-testid="adm-assign-select"
                    className={inputCls + " mb-4"}
                >
                    <option value="">Select technician…</option>
                    {techs.map((t) => (
                        <option key={t.id} value={t.id}>
                            {t.name} — ★{t.rating_avg?.toFixed(1)} ({t.base_city || "—"})
                        </option>
                    ))}
                </select>
                <button
                    onClick={doAssign}
                    data-testid="adm-assign-confirm"
                    disabled={!assignTo}
                    className="lx-btn-gold w-full py-2.5 disabled:opacity-50"
                >
                    Assign
                </button>
            </Modal>

            {/* Reschedule modal */}
            <Modal
                open={!!rescheduleFor}
                onClose={() => setRescheduleFor(null)}
                title={`Reschedule ${rescheduleFor?.booking_number || ""}`}
                testid="adm-reschedule-modal"
            >
                <div className="space-y-4">
                    <div>
                        <label className="lx-label text-white/60 mb-1.5 block">
                            New date/time
                        </label>
                        <input
                            data-testid="adm-reschedule-datetime"
                            type="datetime-local"
                            value={newAt}
                            onChange={(e) => setNewAt(e.target.value)}
                            className={inputCls}
                        />
                    </div>
                    <div>
                        <label className="lx-label text-white/60 mb-1.5 block">
                            Reason (optional)
                        </label>
                        <input
                            data-testid="adm-reschedule-reason"
                            value={rescheduleReason}
                            onChange={(e) => setRescheduleReason(e.target.value)}
                            className={inputCls}
                            placeholder="e.g. Customer asked to postpone"
                        />
                    </div>
                    <button
                        data-testid="adm-reschedule-confirm"
                        onClick={doReschedule}
                        disabled={!newAt}
                        className="lx-btn-gold w-full py-2.5 disabled:opacity-50"
                    >
                        Confirm reschedule
                    </button>
                </div>
            </Modal>

            {/* Manual booking modal */}
            <Modal
                open={manualOpen}
                onClose={() => setManualOpen(false)}
                title="Create manual booking"
                testid="adm-manual-modal"
            >
                <div className="space-y-3">
                    <div>
                        <label className="lx-label text-white/60 mb-1.5 block">Customer</label>
                        <select
                            data-testid="adm-manual-customer"
                            value={manual.customer_id}
                            onChange={(e) =>
                                setManual({ ...manual, customer_id: e.target.value })
                            }
                            className={inputCls}
                        >
                            <option value="">Select customer…</option>
                            {customers.map((c) => (
                                <option key={c.id} value={c.id}>
                                    {c.name} — {c.email}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="lx-label text-white/60 mb-1.5 block">Service</label>
                        <select
                            data-testid="adm-manual-service"
                            value={manual.service_id}
                            onChange={(e) =>
                                setManual({ ...manual, service_id: e.target.value })
                            }
                            className={inputCls}
                        >
                            <option value="">Select service…</option>
                            {services.map((s) => (
                                <option key={s.id} value={s.id}>
                                    {s.name} — {s.base_price}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="lx-label text-white/60 mb-1.5 block">
                            Date / time
                        </label>
                        <input
                            data-testid="adm-manual-datetime"
                            type="datetime-local"
                            value={manual.scheduled_at}
                            onChange={(e) =>
                                setManual({ ...manual, scheduled_at: e.target.value })
                            }
                            className={inputCls}
                        />
                    </div>
                    <div>
                        <label className="lx-label text-white/60 mb-1.5 block">
                            Address line
                        </label>
                        <input
                            data-testid="adm-manual-addr-line1"
                            value={manual.line1}
                            onChange={(e) => setManual({ ...manual, line1: e.target.value })}
                            className={inputCls}
                            placeholder="Villa 22, Marina Walk"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <input
                            data-testid="adm-manual-addr-city"
                            value={manual.city}
                            onChange={(e) => setManual({ ...manual, city: e.target.value })}
                            className={inputCls}
                            placeholder="City"
                        />
                        <input
                            data-testid="adm-manual-addr-country"
                            value={manual.country}
                            onChange={(e) =>
                                setManual({ ...manual, country: e.target.value })
                            }
                            className={inputCls}
                            placeholder="Country"
                        />
                    </div>
                    <div>
                        <label className="lx-label text-white/60 mb-1.5 block">
                            Technician (optional)
                        </label>
                        <select
                            data-testid="adm-manual-tech"
                            value={manual.technician_id}
                            onChange={(e) =>
                                setManual({ ...manual, technician_id: e.target.value })
                            }
                            className={inputCls}
                        >
                            <option value="">Auto-assign</option>
                            {techs.map((t) => (
                                <option key={t.id} value={t.id}>
                                    {t.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="lx-label text-white/60 mb-1.5 block">
                            Payment method
                        </label>
                        <select
                            data-testid="adm-manual-pay"
                            value={manual.payment_method}
                            onChange={(e) =>
                                setManual({ ...manual, payment_method: e.target.value })
                            }
                            className={inputCls}
                        >
                            {["cash", "wallet", "card", "online"].map((p) => (
                                <option key={p} value={p}>
                                    {p}
                                </option>
                            ))}
                        </select>
                    </div>
                    <button
                        data-testid="adm-manual-submit"
                        onClick={submitManual}
                        className="lx-btn-gold w-full py-2.5"
                    >
                        Create booking
                    </button>
                </div>
            </Modal>
        </div>
    );
}
